// @flow strict

import {
  findIndex,
  flatten,
  identity,
  map,
  pathEq,
  pipe,
  prop,
  reject,
  sortBy,
  uniq,
} from 'ramda';
import {
  sanitizeIndicator,
  groupSkillsByStrand,
  toKebabCase,
} from '../strand-utils.js';
import type { AchievementLevel, Skill, Strand } from '../ald-types'

export const compareSkills = (
  lSkill: AchievementLevel,
  rSkill: AchievementLevel
): number => {
  return lSkill.achievementLevelConfig.order - rSkill.achievementLevelConfig.order;
};

/**
 * Funky CSS math.  Doesn't work if the browser window gets narrower than 1280px!
 * TODO: fix that...that definitely sucks
 * 576 = 520px panel width + 4px borders * 2 + 24px margins * 2
 * 1280 - (576px / panel)x2 panels = 128px distributed on both sides
 *
 * @param {number} index index of the "main" achievement level panel
 * @returns {string} CSS pixel value for left position
 */
export const calculateLeftValue = (index: number): string => {
  const leftValue = 64 - index * 576;
  return leftValue + 'px';
};

export const calculateStartingPanel = (numSkills: number): number => {
  return Math.floor((numSkills - 1) / 2);
};

const skillsToIndicators = ([strand, skills]: [Strand, Skill[]]): [Strand, string[]] => {
  return [
    strand,
    pipe(
      // Sort
      sortBy(prop('indicatorOrder')),
      // Skill[] to string[]
      map(prop('indicator')),
      // filter out duplicates caused by multiple achievement levels being folded into the strand groups
      uniq
    )(skills),
  ];
};

export const getStrandIndicatorGroups = (
  skills: AchievementLevel[]
): Array<[Strand, string[]]> => {
  return pipe(
    // off-grade levels don't align by strand so we filter them out here.
    reject(pathEq(['achievementLevelConfig', 'offGrade'], true)),
    // AchievementLevel[] to Array<Skill[]>
    map(prop('skills')),
    // Array<Skill[]> to Skill[]
    flatten,
    // Skill[] to Array<[Strand, Skill[]]>
    groupSkillsByStrand,
    // Array<[Strand, Skill[]]> to Array<[Strand, string[]]>
    map(skillsToIndicators)
  )(skills);
};

export const getPanelNumber = (
  achievementLevel: ?string,
  skills: AchievementLevel[]
): number | null => {
  if (achievementLevel) {
    const index = findIndex(
      pathEq(['achievementLevelConfig','name'], achievementLevel),
      skills
    );
    return index >= 0 ? index : null;
  } else {
    return null;
  }
};

export const buildGridRowTemplate = (
  strandIndicatorGroups: Array<[Strand, string[]]>
): string => {
  const rows = ['[header] auto', '[info-panel] auto'];

  strandIndicatorGroups.forEach(([strand, indicators]:[Strand, string[]]) => {
    rows.push(`[${toKebabCase(strand)}] auto`);
    indicators.forEach((indicator: string) => {
      rows.push(
        `[${toKebabCase(strand)}-${sanitizeIndicator(indicator)}] auto`
      );
    });
  });

  rows.push('[footer] auto');

  return rows.join(' ');
};
